import React from 'react';
import { Lock, ArrowRight, ListChecks } from 'lucide-react';
import { Card, Badge, Button } from './ds';
import { useIsMobile } from '../hooks/useMediaQuery';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const BRAND = '#1E4FBE';

// Backend liefert die Priorität als String (posture.getPriority) — hier auf
// Badge-Ton + Label abgebildet.
const PRIORITY_META = {
  hoch:    { label: 'Hoch',    tone: 'critical' },
  mittel:  { label: 'Mittel',  tone: 'warning' },
  niedrig: { label: 'Niedrig', tone: 'neutral' },
};

// Voll sichtbare Vorschau-Maßnahme (Titel + Kategorie + Priorität + Empfehlung).
const PreviewMeasure = ({ measure }) => {
  const prio = PRIORITY_META[measure.priority] || PRIORITY_META.niedrig;
  return (
    <div style={{
      border: '1px solid #E7E9EF', borderRadius: 12, padding: '16px 18px', background: '#fff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <Badge tone={prio.tone} dot>{prio.label}</Badge>
        <span style={{ fontSize: 12, color: '#8A93A3' }}>{measure.category}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#101623', lineHeight: 1.45, marginBottom: measure.handlungsempfehlung ? 6 : 0 }}>
        {measure.title}
      </div>
      {measure.handlungsempfehlung && (
        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.55 }}>
          {measure.handlungsempfehlung}
        </div>
      )}
    </div>
  );
};

// Gesperrte Maßnahme: der Titel/die Details werden bewusst NICHT vom Server
// geliefert (AC3) — daher Redaktions-Balken statt echtem Text, plus Schloss.
const LockedMeasure = () => (
  <div style={{
    position: 'relative', border: '1px solid #E7E9EF', borderRadius: 12,
    padding: '16px 18px', background: '#FAFBFC', overflow: 'hidden',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ width: 54, height: 16, borderRadius: 5, background: '#E7E9EF' }} />
      <span style={{ width: 90, height: 12, borderRadius: 5, background: '#EEF0F4' }} />
    </div>
    <div style={{ width: '82%', height: 12, borderRadius: 5, background: '#E7E9EF', marginBottom: 8 }} />
    <div style={{ width: '64%', height: 12, borderRadius: 5, background: '#EEF0F4' }} />
    <div style={{
      position: 'absolute', top: 12, right: 14,
      width: 30, height: 30, borderRadius: 8, background: '#F2F6FE', color: BRAND,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Lock width={15} height={15} />
    </div>
  </div>
);

// Personalisierter Maßnahmen-Teaser (AUD-57) für Free-Nutzer: zeigt die aus dem
// echten Audit abgeleiteten Maßnahmen (Anzahl, Kategorien, 1–2 Vorschau) und macht
// den Teaser selbst zur Upgrade-Fläche (Stripe-Checkout).
const MeasuresTeaser = ({ teaser, onUpgrade, upgrading }) => {
  const isMobile = useIsMobile();
  if (!teaser || !teaser.total) return null;

  const { total, categories = [], preview = [], lockedCount = 0 } = teaser;
  const lockedToShow = Math.min(lockedCount, isMobile ? 2 : 3);

  return (
    <Card padding={0} style={{ marginTop: 16, overflow: 'hidden' }}>
      <div style={{ fontFamily: FONT }}>
        {/* Kopf */}
        <div style={{ padding: isMobile ? '20px 20px 0' : '26px 28px 0', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: '#F2F6FE', color: BRAND,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ListChecks width={22} height={22} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: '#101623', letterSpacing: '-0.01em' }}>
              {total} Maßnahmen aus Ihrem Ergebnis abgeleitet
            </h2>
            <p style={{ margin: 0, fontSize: 13.5, color: '#667085', lineHeight: 1.55 }}>
              Konkrete Schritte, um Ihren Sicherheitsstatus gezielt zu verbessern — priorisiert nach Handlungsbedarf.
            </p>
          </div>
        </div>

        {/* Kategorie-Verteilung (echte Zahlen aus dem Audit) */}
        {categories.length > 0 && (
          <div style={{ padding: isMobile ? '14px 20px 0' : '16px 28px 0', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {categories.map((c) => (
              <span key={c.name} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, color: '#2C3646', background: '#F4F6F9',
                border: '1px solid #E7E9EF', borderRadius: 999, padding: '5px 11px',
              }}>
                {c.name}
                <strong style={{ color: BRAND, fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>{c.count}</strong>
              </span>
            ))}
          </div>
        )}

        {/* Maßnahmen: Vorschau + gesperrt */}
        <div style={{ padding: isMobile ? '18px 20px 0' : '20px 28px 0', display: 'grid', gap: 12 }}>
          {preview.map((m, i) => <PreviewMeasure key={m.dinNr || i} measure={m} />)}
          {Array.from({ length: lockedToShow }).map((_, i) => <LockedMeasure key={`locked-${i}`} />)}
        </div>

        {/* Upgrade-Fläche — der Teaser IST die Upgrade-Fläche (kein generisches Banner) */}
        <div style={{
          margin: isMobile ? '18px 20px 20px' : '22px 28px 26px',
          padding: isMobile ? '18px 18px' : '20px 24px',
          background: '#F2F6FE', border: '1px solid #DCE7FB', borderRadius: 14,
          display: 'flex', flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center', gap: 14,
        }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              background: '#DCE7FB', color: BRAND,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Lock width={19} height={19} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#101623', marginBottom: 2 }}>
                {lockedCount > 0
                  ? `${lockedCount} weitere Maßnahmen freischalten`
                  : 'Maßnahmen-Tracking freischalten'}
              </div>
              <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.5 }}>
                Alle Maßnahmen im Detail, Umsetzungsfortschritt verfolgen und Folge-Audits — mit Audicio&nbsp;Standard (49&nbsp;€/Monat).
              </div>
            </div>
          </div>
          <Button
            size="md"
            onClick={onUpgrade}
            disabled={upgrading}
            trailingIcon={<ArrowRight width={16} height={16} />}
            style={isMobile ? { width: '100%' } : { flexShrink: 0 }}
          >
            {upgrading ? 'Weiterleitung …' : 'Jetzt upgraden'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default MeasuresTeaser;
